import { Row, Space, Table, Typography, Tabs } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "src/store/configureStore";
import { StyledButton } from "src/styled_components/StyledButton";
import { StyledCard } from "src/styled_components/StyledCard";
import useTranslate, { TranslationKey } from "src/utils/useTranslate";
import { NewParametersOrArchiveFoundModal } from "./NewParametersOrArchiveFoundModal";
import { convertToPrecision } from "src/utils/decorator";
import { findArchivedParametersSuccess } from "src/store/actions/common";

const { Text } = Typography;
const { TabPane } = Tabs;

export type ShowNewParametersOrArchiveFoundStateTypes = {
  newParametersFound: boolean;
  archivedParametersFound: boolean;
}


const getTableHeadColTranslatedLabel = (
  input:
    | "ingredients_parameters"
    | "ingredients_category"
    | "ingredients_unit"
    | "processing_unit"
    | "processing_parameters"
    | "processing_category"
    | "properties_parameters"
    | "properties_category"
    | "properties_unit"
    | "properties_method"
    | "properties_standard"
    | "properties_specimen"
    | "characterizations_category"
    | "characterizations_parameters"
    | "characterizations_unit",
  t: (key: TranslationKey) => string
) => {
  if (input === "ingredients_parameters") return t("common.ingredients")
  if (input === "ingredients_category") return t("common.ingredientsCategory")
  if (input === "processing_unit") return t("mapColumns.processingUnit")
  if (input === "processing_parameters")
    return t("mapColumns.processingParameters")
  if (input === "processing_category") return t("mapColumns.processingCategory")
  if (input === "ingredients_unit") return t("common.ingredientsUnit")
  if (input === "properties_category") return t("common.propertyCategory")
  if (input === "properties_parameters") return t("inventory.propertyName")
  if (input === "properties_unit") return t("common.propertyUnit")
  if (input === "properties_method") return "Property Method"
  if (input === "properties_standard") return "Property Standard"
  if (input === "properties_specimen") return "Property Specimen"
  if (input === "characterizations_category")
    return t("mapColumns.characterisationCategory")
  if (input === "characterizations_parameters")
    return t("mapColumns.characterisationParameters")
  if (input === "characterizations_unit")
    return t("mapColumns.characterisationUnit")
  return input
}


export const SelectExperiments = ({ callbacks, data }: any) => {
  const [t] = useTranslate();
  const dispatch = useDispatch()
  const foundArchivedParameters = useSelector((state: StoreState) => state.common.foundArchivedParameters)
  const { workOrderId, unitsData, uploadFormat, parameterMapping, previewData } = data;
  const { setCurrent, setUnitsData, setParameterMapping, uploadData } = callbacks;
  const [showNewParametersOrArchiveFoundModal, setShowNewParametersOrArchiveFoundModal] = useState<ShowNewParametersOrArchiveFoundStateTypes>({
    newParametersFound: false,
    archivedParametersFound: false
  })
  const [errors, setErrors] = useState<any>({ formulations: [], processing: [], characterizations: [], properties: [] });
  const [skipPrompt, setSkipPrompt] = useState(false)
  const [dataset, setDataset] = useState<any>(null)
  const [page, setPage] = useState(1)
  const [oldParameters, setOldParameters] = useState({ formulations: [], processing: [], characterizations: [], properties: [] })
  const datasets: any = { "formulations": t("common.formulation"), "processing": t("common.processing"), "characterizations": t("common.characterizations"), "properties": t("common.properties") }

  const cleanValue = useCallback((value: any) => {
    if (typeof value === "boolean") {
      return String(value)?.[0]?.toUpperCase() + String(value).slice(1)
    } else {
      return convertToPrecision(value, 3)
    }
  }, [])

  useEffect(() => {
    setParameterMapping(Object.entries(previewData || {}).reduce((acc: any, [key, value]: any) => ({ ...acc, [key]: value?.new_parameters }), {}))
    setOldParameters(Object.entries(previewData || {}).reduce((acc: any, [key, value]: any) => ({ ...acc, [key]: value?.old_parameters }), {}))
    setErrors(Object.entries(previewData || {}).reduce((acc: any, [key, value]: any) => ({ ...acc, [key]: value?.errors }), {}))
    if (!!previewData?.formulations?.archived_parameters?.length) {
      dispatch(findArchivedParametersSuccess({ data: previewData?.formulations?.archived_parameters, apiPayload: { id: workOrderId, type: "work-order-fileupload" } }))
    }
  }, [dispatch, previewData, workOrderId, setParameterMapping])

  useEffect(() => {
    if (!!previewData?.formulations?.data?.[1]?.length) {
      setDataset("formulations")
    }
    else if (!!previewData?.processing?.data?.[1]?.length) {
      setDataset("processing")
    }
    else if (!!previewData?.characterizations?.data?.[1]?.length) {
      setDataset("characterizations")
    }
    else if (!!previewData?.properties?.data?.[1]?.length) {
      setDataset("properties")
    }
  }, [previewData])

  const getErrorType = useCallback((dataType: string, errorType: string, errorValue: any) => {
    if (dataType === "formulations") {
      if (errorType === "duplicate_parameters") {
        return <Text type="danger">{`${t("common.duplicateIngredientsFound")} - ${errorValue.join(", ")}.`}</Text>
      }
      else if (errorType === "duplicate_trials") {
        return <Text type="danger">{`${t("common.duplicateExperimentFound")} - ${errorValue.join(", ")}.`}</Text>
      }
      else if (errorType === "invalid_parameters" && !!errorValue) {
        return <Text type="danger">{`${t("common.invalidIngredients")}`}</Text>
      }
      else if (errorType === "ingredients_unit" && !!errorValue) {
        return <Text type="danger">{`${t("common.allIngredientsMustHaveTheSameUnit")}`}</Text>
      }
    }
    else if (dataType === "processing") {
      if (errorType === "duplicate_parameters") {
        return <Text type="danger">{`${t("common.duplicateProcessingParametersFound")} - ${errorValue.join(", ")}.`}</Text>
      }
      else if (errorType === "duplicate_trials") {
        return <Text type="danger">{`${t("common.duplicateExperimentFound")} - ${errorValue.join(", ")}.`}</Text>
      }
      else if (errorType === "invalid_parameters" && !!errorValue) {
        return <Text type="danger">{`${t("common.invalidProcessing")}`}</Text>
      }
    }
    else if (dataType === "characterizations") {
      if (errorType === "duplicate_parameters") {
        return <Text type="danger">{`${t("common.duplicateCharacterisationParametersFound")} - ${errorValue.join(", ")}.`}</Text>
      }
      else if (errorType === "duplicate_trials") {
        return <Text type="danger">{`${t("common.duplicateExperimentFound")} - ${errorValue.join(", ")}.`}</Text>
      }
      else if (errorType === "invalid_parameters" && !!errorValue) {
        return <Text type="danger">{`${t("common.invalidCharacterisation")}`}</Text>
      }
    }
    else if (dataType === "properties") {
      if (errorType === "duplicate_parameters") {
        return <Text type="danger">{`${t("common.duplicatePropertyParametersFound")} - ${errorValue.join(", ")}.`}</Text>
      }
      else if (errorType === "duplicate_trials") {
        return <Text type="danger">{`${t("common.duplicateExperimentFound")} - ${errorValue.join(", ")}.`}</Text>
      }
      else if (errorType === "invalid_parameters" && !!errorValue) {
        return <Text type="danger">{`${t("common.invalidProperties")}`}</Text>
      }
      else if (errorType === "invalid_methods") {
        return errorValue.map((res: any) => (
          <Text type="danger">{`"${res?.method}" method doesn't exist on property "${res?.parameter}"`}</Text>
        ))
      }
    }
  }, [t])

  const getTableData = useMemo(() => {
    const columns = Object.keys(previewData?.[dataset]?.data?.[page]?.[0] || {}).map((key: any) => ({
      dataIndex: key,
      title: getTableHeadColTranslatedLabel(key, t),
      key,
      width: 150,
      render: (text: any, record: any, index: any) => {
        return cleanValue(text)
      }
    }))
    const data = previewData?.[dataset]?.data?.[page] || []
    const pageSize = data?.length
    const total = Object.keys(previewData?.[dataset]?.data || {}).length * pageSize
    return { columns, data, total, pageSize }
  }, [previewData, dataset, page, cleanValue, t])

  const onSubmit = () => {
    if (!skipPrompt && (!!foundArchivedParameters?.data?.length ||
      (!!parameterMapping?.formulations?.length || !!parameterMapping?.processing?.length || !!parameterMapping?.characterizations?.length || !!parameterMapping?.properties?.length)
    )) {
      setShowNewParametersOrArchiveFoundModal((prev) => ({
        ...prev,
        ...(!!foundArchivedParameters?.data?.length && { archivedParametersFound: true }),
        ...((!!parameterMapping?.formulations?.length || !!parameterMapping?.processing?.length || !!parameterMapping?.characterizations?.length || !!parameterMapping?.properties?.length) &&
          { newParametersFound: true })
      }))
      return
    }
    if (!!unitsData?.length) {
      setCurrent(4)
    } else {
      uploadData()
    }
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <StyledCard>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          {/* <div>
            <Search
              placeholder={t("common.search")}
              onSearch={(value) => setSearchText(value)}
              style={{ width: 200, marginBottom: 16 }}
            />
          </div> */}
          <Tabs size="large" onChange={e => { setPage(1); setDataset(e) }}>
            {Object.entries(datasets || {}).filter(([key, value]: any) => !!previewData?.[key]?.data?.[1]?.length).map(([key, value]: any) => (
              <TabPane
                tab={value}
                key={key}
                style={{ width: "100%", overflow: "auto" }}
              >
                <Table
                  columns={getTableData?.columns}
                  dataSource={getTableData?.data}
                  bordered
                  pagination={{
                    pageSize: getTableData?.pageSize,
                    current: page,
                    total: getTableData?.total,
                    onChange: (page: number) => setPage(page),
                    showSizeChanger: false,
                    responsive: true,
                    position: ["bottomRight"]
                  }}
                  scroll={{ y: 420 }}
                />
              </TabPane>
            ))}
          </Tabs>
          <Space direction="vertical" style={{ width: "100%" }} size="small">
            {Object.entries(errors || {})
              .filter(([key, value]: any) => !!Object.keys(value || {}).length)
              .map(([key, value]: any) => (
                <Space direction="vertical" style={{ width: "100%" }} size="small">
                  <Text strong type="secondary">{`${datasets?.[key]} :`} </Text>
                  {Object.entries(value).map(([errorType, errorValue]: any) => (
                    getErrorType(key, errorType, errorValue)
                  ))}
                </Space>
              ))}
          </Space>
        </Space>
      </StyledCard>
      <Row justify="end">
        <Space>
          <StyledButton
            onClick={() => {
              window.open("/inventory/repository/properties", "_blank")
            }}
            danger
            size="large"
          >{`${t("common.open")} ${t("common.repository")}`}</StyledButton>
          <StyledButton
            onClick={() => {
              if (uploadFormat === "default") {
                setCurrent(2)
              } else {
                setCurrent(0)
              }
              setUnitsData([])
            }}
            size="large"
          >{uploadFormat === "default" ? `${t("common.back")}: ${t("common.mapColumns")}` : `${t("common.back")}: ${t("common.uploadAFile")}`}</StyledButton>
          <StyledButton
            disabled={!!Object.entries(errors || {}).some(([key, value]: any) => !!Object.keys(value || {}).length)}
            onClick={onSubmit}
            size="large"
            type="primary"
          >{`${t("common.next")}: ${!!unitsData?.length
            ? t("common.mapUnits")
            : t("common.confirmUpload")
            }`}</StyledButton>
        </Space>
      </Row>
      {(showNewParametersOrArchiveFoundModal.newParametersFound || showNewParametersOrArchiveFoundModal.archivedParametersFound)
        && <NewParametersOrArchiveFoundModal
          parameterMapping={parameterMapping}
          setParameterMapping={setParameterMapping}
          oldParameters={oldParameters}
          showNewParametersOrArchiveFoundModal={showNewParametersOrArchiveFoundModal}
          setShowNewParametersOrArchiveFoundModal={setShowNewParametersOrArchiveFoundModal}
          setSkipPrompt={setSkipPrompt}
        />}
    </Space>
  );
};
